<template>
  <div>
    <PageTitle pageTitle="Security" subTitle="Error" />
    <v-row class="pa-4">
      <NavButton routeName="goToAccount" icon="mdi-account" @navigate="navigateTo"
        :active="isActiveRoute('goToAccount')">Account</NavButton>
      <NavButton routeName="goToAccountSecurity" icon="mdi-shield-lock" @navigate="navigateTo"
        :active="isActiveRoute('goToAccountSecurity')">Security</NavButton>
      <NavButton routeName="goToImages" icon="mdi-image" @navigate="navigateTo" :active="isActiveRoute('goToImages')">
        Image</NavButton>
    </v-row>
    <router-view></router-view>
    <!-- <div class="row justify-content-center"> -->
    <v-row>
      <v-container>
        <v-card class="pa-4">
          <v-card-title class="text-h5">Update Password</v-card-title>
          <v-card-text>
            <v-form @submit.prevent="submitPassword" v-model="isFormValid">
              <v-row>
                <v-col col="12" md="4">
                  <v-text-field v-model="form.oldPassword" label="Old Password" outlined dense
                    :type="showPassword ? 'text' : 'password'" :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'" @click:append-inner="togglePassword"
                    required></v-text-field>
                </v-col>
                <v-col col="12" md="4">
                  <v-text-field v-model="form.newPassword" label="New Password" outlined dense :rules="passwordRules"
                    :type="showPassword ? 'text' : 'password'" :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'" @click:append-inner="togglePassword"
                    required></v-text-field></v-col>
                <v-col col="12" md="4">
                  <v-text-field v-model="form.retypeNewPassword" label="Retype New Password" outlined dense
                    :rules="confirmPasswordRules" :type="showPassword ? 'text' : 'password'" :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'" @click:append-inner="togglePassword"
                    required></v-text-field>
                </v-col>
              </v-row>
              <!-- Submit Button -->
              <v-row>
                <v-col cols="12" md="4">
                  <v-btn color="primary" :disabled="!isFormValid" class="mt-4" type="submit" block>
                    Update Password
                  </v-btn>
                </v-col>
              </v-row>
              <!-- Password Requirements -->
              <v-row class="mt-4">
                <v-col cols="12" md="6">
                  <v-list dense>
                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-title>
                          Password requirements:
                        </v-list-item-title>

                        <v-list-item-subtitle>
                          • Minimum 8 characters long — the more, the better
                        </v-list-item-subtitle>
                        <v-list-item-subtitle>
                          • At least one lowercase character
                        </v-list-item-subtitle>
                        <v-list-item-subtitle>
                          • At least one uppercase character
                        </v-list-item-subtitle>
                        <v-list-item-subtitle>
                          • At least one number
                        </v-list-item-subtitle>
                        <v-list-item-subtitle>
                          • At least one symbol (e.g., !@#$%^&)
                        </v-list-item-subtitle>
                        <v-list-item-subtitle>
                          • Your password can't be entirely numeric
                        </v-list-item-subtitle>
                        <v-list-item-subtitle>
                          • Avoid using common patterns like "123456"
                        </v-list-item-subtitle>
                      </v-list-item-content>

                    </v-list-item>
                  </v-list>
                </v-col>
                <v-col cols="12" md="6">
                  <!-- Warning Message -->
                  <v-alert type="warning" class="mt-4" outlined>
                    WARNING: You won't be able to view your password after
                    updating. Make sure you memorize your password. Just in
                    case, be sure to copy your updated password.
                  </v-alert>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
        </v-card>
      </v-container>
    </v-row>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import PageTitle from "@/components/Common/PageTitle.vue";
import NavButton from "@/components/Others/MyProfile/NavButton.vue";
import { useRouter, useRoute } from "vue-router";
import { changePassword, changeProfileData } from "@/services/apiServices";
import Swal from "sweetalert2";
const router = useRouter();
const route = useRoute();


const showPassword = ref(false);
const form = ref({
  oldPassword: "",
  newPassword: "",
  retypeNewPassword: "",
});

const isFormValid = ref(false);

// Password validation rules
const passwordRules = [
  (v) => !!v || "Password is required",
  (v) => v.length >= 8 || "Password must be at least 8 characters long - the more, the better",
  (v) => /[a-z]/.test(v) || "Password must contain at least one lowercase letter",
  (v) => /[A-Z]/.test(v) || "Password must contain at least one uppercase letter",
  (v) => /[\d]/.test(v) || "Password must contain at least one number",
  (v) => /[!@#$%^&*()_+={}[\]|:;,.?/<>'"\\-]/.test(v) || "Password must contain at least one symbol",
  (v) => !/^\d+$/.test(v) || "Password cannot be entirely numeric",
  (v) => !/123456/.test(v) || "Password is too common or predictable",
];



const confirmPasswordRules = [
  (v) => !!v || "Please retype your new password",
  (v) => v === form.value.newPassword || "Passwords do not match",
];

const togglePassword = () => {
  showPassword.value = !showPassword.value;
};



// Handle form submission
const submitPassword = async () => {
  if (form.value.newPassword !== form.value.retypeNewPassword) {
    Swal.fire({
      icon: "error",
      title: "Password Mismatch",
      text: "New password does not match.",
      confirmButtonColor: "#d33",
      toast: true,
      position: "top-end",
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
    });

    return;
  }

  // loading.value = true;

  try {
    await changePassword({
      old_password: form.value.oldPassword,
      new_password: form.value.newPassword,
    });
    Swal.fire({
      icon: "success",
      title: "Password Updated",
      text: "Your password has been successfully updated.",
      confirmButtonColor: "#4CAF50",
      toast: true,
      position: "top-end",
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
    });
    form.value = {
      oldPassword: "",
      newPassword: "",
      retypeNewPassword: "",
    };

    router.push({ name: "admin" });
  } catch (error) {
    alert(
      error.response?.data?.message ||
      "Failed to update password. Please try again."
    );
  }
};

const navigateTo = (routeName) => {
  router.push({ name: routeName });
};

// Function to check if the current route is active
const isActiveRoute = (routeName) => {
  return route.name === routeName;
};



</script>

<style scoped>
.v-alert {
  font-size: 14px;
}
</style>
